import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Lock, Shield, RefreshCw, Unlock, Ban, CalendarClock, Server, Copy, Send,
  Eye, Plus, KeyRound, ScrollText, X, LogOut, Megaphone, Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  { id: "plan-filmes-series-monthly", label: "Filmes e Séries — 30 dias" },
  { id: "plan-filmes-monthly", label: "Filmes — 30 dias" },
  { id: "plan-series-monthly", label: "Séries — 30 dias" },
  { id: "plan-filmes-series-once", label: "Filmes e Séries — 1 sincronia" },
  { id: "plan-filmes-once", label: "Filme — 1 sincronia" },
  { id: "plan-series-once", label: "Série — 1 sincronia" },
];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("wx_admin_token") || "");
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [details, setDetails] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showAvisos, setShowAvisos] = useState(false);
  const [audit, setAudit] = useState([]);

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/codes`, { headers: { Authorization: `Bearer ${token}` } });
      setCodes(data.codes || []);
    } catch (_) {
      toast.error("Sessão expirada. Faça login novamente.");
      setToken(""); localStorage.removeItem("wx_admin_token");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const login = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/login`, { user, password: pass });
      setToken(data.token); localStorage.setItem("wx_admin_token", data.token);
      toast.success("Bem-vindo, admin!");
    } catch (_) { toast.error("Usuário ou senha inválidos."); }
  };
  const logout = () => { setToken(""); localStorage.removeItem("wx_admin_token"); };

  const setupWebhook = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/telegram/setup-webhook`, {}, auth());
      if (data.ok) toast.success("Webhook do Telegram configurado!");
      else toast.error("Falha ao configurar webhook. Veja o console.");
      console.log("setup-webhook:", data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao configurar webhook."); }
  };

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erro na operação."); } };
  const setExpiry = (code) => { const d = prompt("Nova data de vencimento (AAAA-MM-DD):"); if (d) act(() => axios.post(`${API}/admin/panel/${code}/set-expiry`, { date: d }, auth()), "Data atualizada."); };
  const activate = (code) => { const dd = prompt("Liberar por quantos dias?", "30"); if (dd) act(() => axios.post(`${API}/admin/panel/${code}/activate`, { days: parseInt(dd, 10) }, auth()), "Cliente liberado."); };
  const block = (code) => { if (window.confirm(`Bloquear o cliente ${code}?`)) act(() => axios.post(`${API}/admin/panel/${code}/block`, {}, auth()), "Cliente bloqueado."); };
  const setIp = (code) => { const ip = prompt("Novo IP do servidor:"); if (ip) act(() => axios.post(`${API}/admin/panel/${code}/set-ip`, { ip }, auth()), "IP alterado."); };
  const unlockIp = (code) => act(() => axios.post(`${API}/admin/panel/${code}/ip-unlock`, {}, auth()), "Troca de IP liberada (uso único).");
  const copyLink = (code) => { navigator.clipboard.writeText(`${BASE}/panel/${code}`); toast.success("Link do painel copiado!"); };
  const sendTg = (code) => { const t = prompt("Mensagem para o Telegram do cliente:"); if (t) act(() => axios.post(`${API}/admin/panel/${code}/telegram`, { text: t }, auth()), "Mensagem enviada."); };

  const openDetails = async (code) => {
    try { const { data } = await axios.get(`${API}/admin/panel/${code}/details`, auth()); setDetails(data); }
    catch (e) { toast.error("Não foi possível carregar os detalhes."); }
  };
  const openAudit = async () => {
    try { const { data } = await axios.get(`${API}/admin/audit`, auth()); setAudit(data.audit || []); setShowAudit(true); }
    catch (e) { toast.error("Não foi possível carregar a auditoria."); }
  };

  const filtered = codes.filter((c) => { const s = q.trim().toLowerCase(); return !s || c.code.toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s) || (c.status || "").toLowerCase().includes(s); });

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
        <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900 p-8">
          <div className="mb-6 flex items-center gap-2 text-emerald-400"><Shield className="h-6 w-6" /><h1 className="font-display text-xl font-bold text-white">WX SyncVods — Admin</h1></div>
          <div className="space-y-3">
            <div><Label className="text-slate-300">Usuário</Label><Input data-testid="admin-user" value={user} onChange={(e) => setUser(e.target.value)} className="mt-1 bg-slate-800 text-white" /></div>
            <div><Label className="text-slate-300">Senha</Label><Input data-testid="admin-pass" type="password" value={pass} onChange={(e) => setPass(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} className="mt-1 bg-slate-800 text-white" /></div>
            <Button data-testid="admin-login-btn" onClick={login} className="w-full bg-emerald-600 hover:bg-emerald-700"><Lock className="mr-2 h-4 w-4" />Entrar</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-200 sm:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-white"><Shield className="h-6 w-6 text-emerald-400" />Painel Admin — Clientes</h1>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="admin-create-open" onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="mr-2 h-4 w-4" />Gerar código</Button>
          <Button data-testid="admin-audit-open" onClick={openAudit} variant="outline" className="border-slate-700 bg-slate-800 text-white hover:bg-slate-700"><ScrollText className="mr-2 h-4 w-4" />Auditoria</Button>
          <Button data-testid="admin-avisos-open" onClick={() => setShowAvisos(true)} variant="outline" className="border-slate-700 bg-slate-800 text-white hover:bg-slate-700"><Megaphone className="mr-2 h-4 w-4" />Avisos</Button>
          <Button data-testid="admin-changepass-open" onClick={() => setShowPass(true)} variant="outline" className="border-slate-700 bg-slate-800 text-white hover:bg-slate-700"><KeyRound className="mr-2 h-4 w-4" />Senha</Button>
          <Button data-testid="admin-refresh" onClick={load} variant="outline" className="border-slate-700 bg-slate-800 text-white hover:bg-slate-700"><RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />Atualizar</Button>
          <Button data-testid="admin-webhook-setup" onClick={setupWebhook} variant="outline" className="border-slate-700 bg-slate-800 text-white hover:bg-slate-700"><Send className="mr-2 h-4 w-4" />Webhook TG</Button>
          <Button data-testid="admin-logout" onClick={logout} variant="outline" className="border-slate-700 bg-slate-800 text-white hover:bg-slate-700"><LogOut className="mr-2 h-4 w-4" />Sair</Button>
        </div>
      </div>
      <div className="mb-4">
        <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por código, e-mail ou status..." className="max-w-md bg-slate-800 text-white" />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-800">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-900 text-slate-400"><tr>
            <th className="p-3">Código</th><th className="p-3">Status</th><th className="p-3">Vence</th><th className="p-3">Sistema</th><th className="p-3">IP</th><th className="p-3">Ações</th>
          </tr></thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.code} data-testid={`row-${c.code}`} className="border-t border-slate-800 hover:bg-slate-900/50">
                <td className="p-3 font-mono-code text-xs text-white">{c.code}
                  {c.is_trial && <span data-testid={`badge-trial-${c.code}`} className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-300 align-middle">Teste</span>}
                  <div className="text-[10px] text-slate-500">{c.email || ""}</div></td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.status === "ativo" ? "bg-emerald-900 text-emerald-300" : c.status === "suspenso" ? "bg-red-900 text-red-300" : "bg-slate-700 text-slate-300"}`}>{c.status}</span></td>
                <td className="p-3 text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("pt-BR") : "—"}</td>
                <td className="p-3 text-xs">{c.system_type || "—"}</td>
                <td className="p-3 font-mono-code text-xs">{c.main_ip || "—"} {c.ip_locked ? <Lock className="inline h-3 w-3 text-amber-400" /> : null}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    <button data-testid={`act-details-${c.code}`} onClick={() => openDetails(c.code)} title="Ver detalhes" className="rounded bg-slate-600 px-2 py-1 text-xs hover:bg-slate-500"><Eye className="h-3.5 w-3.5" /></button>
                    <button data-testid={`act-expiry-${c.code}`} onClick={() => setExpiry(c.code)} title="Alterar data" className="rounded bg-sky-700 px-2 py-1 text-xs hover:bg-sky-600"><CalendarClock className="h-3.5 w-3.5" /></button>
                    <button data-testid={`act-activate-${c.code}`} onClick={() => activate(c.code)} title="Liberar" className="rounded bg-emerald-700 px-2 py-1 text-xs hover:bg-emerald-600"><Unlock className="h-3.5 w-3.5" /></button>
                    <button data-testid={`act-block-${c.code}`} onClick={() => block(c.code)} title="Bloquear" className="rounded bg-red-700 px-2 py-1 text-xs hover:bg-red-600"><Ban className="h-3.5 w-3.5" /></button>
                    <button data-testid={`act-setip-${c.code}`} onClick={() => setIp(c.code)} title="Alterar IP" className="rounded bg-slate-700 px-2 py-1 text-xs hover:bg-slate-600"><Server className="h-3.5 w-3.5" /></button>
                    <button data-testid={`act-unlockip-${c.code}`} onClick={() => unlockIp(c.code)} title="Liberar troca de IP" className="rounded bg-amber-700 px-2 py-1 text-xs hover:bg-amber-600"><Unlock className="h-3.5 w-3.5" /></button>
                    <button data-testid={`act-copy-${c.code}`} onClick={() => copyLink(c.code)} title="Copiar link do painel" className="rounded bg-indigo-700 px-2 py-1 text-xs hover:bg-indigo-600"><Copy className="h-3.5 w-3.5" /></button>
                    <button data-testid={`act-tg-${c.code}`} onClick={() => sendTg(c.code)} title="Enviar mensagem no Telegram" className="rounded bg-cyan-700 px-2 py-1 text-xs hover:bg-cyan-600"><Send className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-slate-500">Total: {codes.length} cliente(s). Ícones: 👁️ detalhes · 🗓️ data · 🔓 liberar · ⛔ bloquear · 🖥️ alterar IP · 🔑 liberar troca de IP · 📋 copiar link · ✈️ Telegram.</p>

      {details && <DetailsModal d={details} onClose={() => setDetails(null)} copyLink={copyLink} />}
      {showCreate && <CreateModal auth={auth} onClose={() => setShowCreate(false)} onDone={load} />}
      {showPass && <PasswordModal auth={auth} onClose={() => setShowPass(false)} onToken={(t) => { setToken(t); localStorage.setItem("wx_admin_token", t); }} />}
      {showAudit && <AuditModal audit={audit} onClose={() => setShowAudit(false)} />}
      {showAvisos && <AvisosModal auth={auth} onClose={() => setShowAvisos(false)} />}
    </div>
  );
}

const Overlay = ({ children, onClose, testid }) => (
  <div data-testid={testid} className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/60 p-4 backdrop-blur-sm">
    <div className="my-8 w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl">{children}</div>
    <button className="hidden" onClick={onClose} />
  </div>
);

const Header = ({ title, onClose }) => (
  <div className="mb-4 flex items-center justify-between">
    <h2 className="font-display text-lg font-bold text-white">{title}</h2>
    <button data-testid="modal-close" onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-800 hover:text-white"><X className="h-5 w-5" /></button>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b border-slate-800 py-1.5 text-sm">
    <span className="text-slate-400">{k}</span><span className="break-all text-right font-medium text-slate-200">{v || "—"}</span>
  </div>
);

function DetailsModal({ d, onClose, copyLink }) {
  const sc = d.server_config || {};
  const job = d.sync_job || {};
  return (
    <Overlay onClose={onClose} testid="details-modal">
      <Header title={`Cliente ${d.code}`} onClose={onClose} />
      <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
        <Row k="E-mail" v={d.email} />
        <Row k="Status" v={d.status} />
        <Row k="Plano" v={d.plan_title} />
        <Row k="Sistema" v={d.system_type} />
        <Row k="Vence em" v={d.expires_at ? new Date(d.expires_at).toLocaleString("pt-BR") : "—"} />
        <Row k="Ativado em" v={d.activated_at ? new Date(d.activated_at).toLocaleString("pt-BR") : "—"} />
        <Row k="IP do servidor" v={`${sc.main_ip || "—"}${d.ip_locked ? " 🔒" : ""}`} />
        <Row k="Porta / Banco" v={`${sc.db_port || "—"} / ${sc.db_name || "—"}`} />
        <Row k="Usuário do banco" v={sc.db_user} />
        <Row k="Telegram Chat ID" v={d.telegram} />
        <Row k="Auto-sync" v={d.sync_auto?.enabled ? `Ligada às ${d.sync_auto.hour}` : "Desligada"} />
        <Row k="Última sync" v={job.status || "—"} />
      </div>

      <div className="mt-4">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Fontes M3U ({(d.sources || []).length})</p>
        <div className="max-h-24 overflow-auto rounded-lg bg-slate-950 p-2 font-mono-code text-[11px] text-emerald-300">
          {(d.sources || []).length ? d.sources.map((s, i) => <div key={i} className="break-all">{s}</div>) : <span className="text-slate-500">Nenhuma fonte cadastrada.</span>}
        </div>
      </div>

      {job.error && <div className="mt-3 rounded-lg border border-red-800 bg-red-950/50 p-2 text-xs text-red-300">Erro da última sincronização: {job.error}</div>}

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-400">Logs Filmes / TMDB</p>
          <div data-testid="details-log-movies" className="h-40 overflow-auto rounded-lg bg-slate-950 p-2 font-mono-code text-[11px] text-amber-200">
            {(job.log || []).length ? job.log.map((l, i) => <div key={i}>{l}</div>) : <span className="text-slate-500">Sem logs.</span>}
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-sky-400">Logs Séries</p>
          <div className="h-40 overflow-auto rounded-lg bg-slate-950 p-2 font-mono-code text-[11px] text-sky-200">
            {(job.series_log || []).length ? job.series_log.map((l, i) => <div key={i}>{l}</div>) : <span className="text-slate-500">Sem logs.</span>}
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button data-testid="details-copy-link" onClick={() => copyLink(d.code)} className="bg-indigo-600 hover:bg-indigo-700"><Copy className="mr-2 h-4 w-4" />Copiar link do painel</Button>
      </div>
    </Overlay>
  );
}

function CreateModal({ auth, onClose, onDone }) {
  const [plan, setPlan] = useState("plan-filmes-series-monthly");
  const [days, setDays] = useState("30");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const create = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/codes/create`, { plan_id: plan, days: parseInt(days, 10) || 30, email }, auth());
      setResult(data); onDone(); toast.success("Código gerado!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao gerar código."); }
  };
  return (
    <Overlay onClose={onClose} testid="create-modal">
      <Header title="Gerar novo código de acesso" onClose={onClose} />
      {!result ? (
        <div className="space-y-3">
          <div><Label className="text-slate-300">Plano</Label>
            <select data-testid="create-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 p-2 text-sm text-white">
              {PLANS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div><Label className="text-slate-300">Dias de validade</Label><Input data-testid="create-days" value={days} onChange={(e) => setDays(e.target.value)} className="mt-1 bg-slate-800 text-white" /></div>
          <div><Label className="text-slate-300">E-mail (opcional)</Label><Input data-testid="create-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-slate-800 text-white" /></div>
          <Button data-testid="create-submit" onClick={create} className="w-full bg-emerald-600 hover:bg-emerald-700"><Plus className="mr-2 h-4 w-4" />Gerar código</Button>
        </div>
      ) : (
        <div className="space-y-3 text-sm" data-testid="create-result">
          <Row k="Código" v={result.code} />
          <Row k="Vence em" v={new Date(result.expires_at).toLocaleString("pt-BR")} />
          <div className="rounded-lg bg-slate-950 p-2 font-mono-code text-[11px] text-emerald-300 break-all">{result.link}</div>
          <Button onClick={() => { navigator.clipboard.writeText(result.link); toast.success("Link copiado!"); }} className="w-full bg-indigo-600 hover:bg-indigo-700"><Copy className="mr-2 h-4 w-4" />Copiar link</Button>
        </div>
      )}
    </Overlay>
  );
}

function PasswordModal({ auth, onClose, onToken }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const save = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/change-password`, { current: cur, new: nw }, auth());
      if (data.token && onToken) onToken(data.token);
      toast.success("Senha alterada!"); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao alterar a senha."); }
  };
  return (
    <Overlay onClose={onClose} testid="password-modal">
      <Header title="Trocar senha do admin" onClose={onClose} />
      <div className="space-y-3">
        <div><Label className="text-slate-300">Senha atual</Label><Input data-testid="pass-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1 bg-slate-800 text-white" /></div>
        <div><Label className="text-slate-300">Nova senha (mín. 6)</Label><Input data-testid="pass-new" type="password" value={nw} onChange={(e) => setNw(e.target.value)} className="mt-1 bg-slate-800 text-white" /></div>
        <Button data-testid="pass-submit" onClick={save} className="w-full bg-emerald-600 hover:bg-emerald-700"><KeyRound className="mr-2 h-4 w-4" />Salvar nova senha</Button>
      </div>
    </Overlay>
  );
}

function AuditModal({ audit, onClose }) {
  return (
    <Overlay onClose={onClose} testid="audit-modal">
      <Header title="Log de auditoria" onClose={onClose} />
      <div className="max-h-[60vh] overflow-auto">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-slate-900 text-slate-400"><tr><th className="p-2">Quando</th><th className="p-2">Ação</th><th className="p-2">Código</th><th className="p-2">Detalhe</th><th className="p-2">IP</th></tr></thead>
          <tbody>
            {audit.map((a, i) => (
              <tr key={i} className="border-t border-slate-800">
                <td className="p-2 text-slate-400">{a.at ? new Date(a.at).toLocaleString("pt-BR") : "—"}</td>
                <td className="p-2 font-semibold text-emerald-300">{a.action}</td>
                <td className="p-2 font-mono-code text-slate-300">{a.code || "—"}</td>
                <td className="p-2 text-slate-400">{a.detail || "—"}</td>
                <td className="p-2 font-mono-code text-slate-500">{a.ip || "—"}</td>
              </tr>
            ))}
            {!audit.length && <tr><td colSpan={5} className="p-4 text-center text-slate-500">Nenhum registro de auditoria ainda.</td></tr>}
          </tbody>
        </table>
      </div>
    </Overlay>
  );
}


function AvisosModal({ auth, onClose }) {
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const { data } = await axios.get(`${API}/admin/announcements`, auth()); setList(data.announcements || []); }
    catch (e) { toast.error("Falha ao carregar avisos."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const add = async () => {
    if (!text.trim()) return;
    try { await axios.post(`${API}/admin/announcements`, { text: text.trim() }, auth()); setText(""); toast.success("Aviso publicado!"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha ao publicar."); }
  };
  const del = async (id) => {
    try { await axios.delete(`${API}/admin/announcements/${id}`, auth()); toast.success("Aviso removido."); load(); }
    catch (e) { toast.error("Falha ao remover."); }
  };
  return (
    <Overlay onClose={onClose} testid="avisos-modal-admin">
      <Header title="Avisos do site" onClose={onClose} />
      <p className="mb-3 text-xs text-slate-400">Estes avisos aparecem no modal "Avisos" do site, para todos os clientes.</p>
      <div className="mb-4 flex gap-2">
        <Input data-testid="aviso-input" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Escreva um novo aviso / atualização..." className="bg-slate-800 text-white" />
        <Button data-testid="aviso-add" onClick={add} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="max-h-[50vh] space-y-2 overflow-auto">
        {loading ? <p className="text-sm text-slate-500">Carregando…</p> : list.length ? list.map((a) => (
          <div key={a.id} data-testid={`aviso-row-${a.id}`} className="flex items-start justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950 p-3">
            <span className="text-sm text-slate-200">{a.text}</span>
            <button data-testid={`aviso-del-${a.id}`} onClick={() => del(a.id)} className="shrink-0 rounded bg-red-700 px-2 py-1 text-xs hover:bg-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )) : <p className="text-sm text-slate-500">Nenhum aviso ainda.</p>}
      </div>
    </Overlay>
  );
}
